import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";

import { AddButton } from "../../../../components/Button";
import CommonDataGrid from "../../../../components/CommonDataGrid";
import { Column } from "../../../../components/CommonDataGrid/props";
import Loader from "../../../../components/Loader";
import Modal from "../../../../components/Model";
import VideoModal from "../../../../components/VideoModal";
import Chat from "../../../../images/SideBar/Chat";
import EditImg from "../../../../images/SideBar/Edit";
import UserPlayIcon from "../../../../images/SideBar/UserPlay";
import VideoIcon from "../../../../images/SideBar/Video";
import { formatDate, style, stylePriority } from "../../../../util/constant";
import ScreenRecorder from "../../../Admin/ScreenRecorder";
import useTaskSheet from "../useTasksheet";
import EditTaskSheetIndex from "./EditTasksheet/index";
import ApiService from "../../../../service/ApiService";
import { apiPaths } from "../../../../service/apiPaths";

const TaskSheet: React.FC = () => {
  const {
    selectedTaskSheetId,
    taskSheetData,
    showConfirmation,
    handleCancelStatusUp,
    updateScreen,
    fetchAllTaskSheets,
    handleEdit,
    handleAdd,
    handleDateFilter,
    handleTomorrowDay,
    handleSelectStatus,
    setSearch,
    setPage,
    setYesterPage,
    setBackPage,
    setTomorrowPage,
    tomorrowPage,
    page,
    yesterPage,
    backPage,
    loading,
    totalPage,
    yesterDayData,
    tomorrowDayData,
    backLogData,
    yesterDayPage,
    tomorrowDayPage,
    backLogPage,
    handleYesterDay,
    handleBackLog,
    showModal,
    description,
    setDescription,
    handleModalSubmit,
    handleModalClose,
    adminComment,
    taskSummary,
    sendSlackMessage,
    setShowModal,
    selectedId,
    setSelectedId,
    showWelcomeModal,
    setShowWelcomeModal,
    latestAdminComment,
    latestAdminCommentId,
    setLatestAdminCommentId,
    welcomeMessageSent,
    setWelcomeMessageSent,
  } = useTaskSheet();

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState("");
  const [showScreenRecorder, setShowScreenRecorder] = useState(false);
  const [selectedTaskForVideo, setSelectedTaskForVideo] = useState<any>(null);
  const [isSending, setIsSending] = useState(false);
  const [commentMessage, setCommentMessage] = useState("");
  const [isSendingComment, setIsSendingComment] = useState(false);
  const isSlackRequestInProgress = useRef(false);


  const handleVideoClick = (videoUrl: string) => {
    setSelectedVideoUrl(videoUrl);
    setShowVideoModal(true);
  };

  const handleCopyAllData = async () => {
    try {
      setIsSending(true);
      toast.loading("Fetching all tasks...");

      const today = new Date();
      const allTasks = await fetchAllTaskSheets(today, today);

      if (!allTasks || allTasks.length === 0) {
        toast.dismiss();
        toast.error("No data to copy!");
        return;
      }

      const formattedData = formatTaskData(allTasks);

      const textArea = document.createElement("textarea");
      textArea.value = formattedData;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      toast.dismiss();

      if (successful) {
        toast.success("Today's tasks copied successfully!");
      } else {
        toast.error("Copy failed");
      }
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to copy data");
      console.error(error);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendToSlack = async (
    fromDate?: Date | null,
    toDate?: Date | null
  ) => {
    // 🔒 HARD LOCK
    if (isSlackRequestInProgress.current) return;
    isSlackRequestInProgress.current = true;

    try {
      setIsSending(true);
      toast.dismiss();
      toast.loading("Sending tasks to Slack...");

      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.dismiss();
        toast.error("Please login to send messages to Slack");
        return;
      }

      const decodedToken: any = jwtDecode(token);
      const userId = decodedToken.userId;
      if (!userId) {
        toast.dismiss();
        toast.error("User ID not found");
        return;
      }

      const userData = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getAllEmployees}/${userId}`,
      });

      if (!userData?.slackChannelId) {
        toast.dismiss();
        toast.error("Slack channel not configured. Please contact admin.");
        return;
      }

      const today = new Date();
      const allTasks = await fetchAllTaskSheets(
        fromDate ?? today,
        toDate ?? today
      );
      if (!allTasks.length) {
        toast.dismiss();
        toast.error("No data to send!");
        return;
      }

      const formattedData = formatTaskData(allTasks);

      await ApiService({
        method: "POST",
        endpoint: apiPaths.slackChatPostTodayMessage,
        data: {
          channel: userData.slackChannelId,
          text: formattedData,
        },
      });

      toast.dismiss();
      toast.success("Tasks sent to Slack successfully!");
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to send data to Slack");
      console.error(error);
    } finally {
      // 🔓 RELEASE LOCK
      isSlackRequestInProgress.current = false;
      setIsSending(false);
    }
  };

  const handleSendCommentToSlack = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isSendingComment || !commentMessage.trim()) {
      toast.error("Please enter a message to send");
      return;
    }

    // Validate that we have a comment ID
    if (!latestAdminCommentId) {
      toast.error("Unable to send message. Please try again.");
      return;
    }

    try {
      setIsSendingComment(true);
      toast.loading("Sending comment to Slack...");

      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.dismiss();
        toast.error("Please login again");
        return;
      }

      const decodedToken: any = jwtDecode(token);
      const userId = decodedToken.userId;

      const userData = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getAllEmployees}/${userId}`,
      });

      if (!userData?.slackChannelId) {
        toast.dismiss();
        toast.error("Slack channel not configured");
        return;
      }

      const slackMessage = {
        channel: userData.slackChannelId,
        text: `📢 *Admin Comment:* ${latestAdminComment}

💬 *User Reply:* ${commentMessage}`,
      };

      await ApiService({
        method: "POST",
        endpoint: apiPaths.sendCommentMessage,
        data: slackMessage,
      });

      toast.dismiss();

      try {
        await ApiService({
          method: "PUT",
          endpoint: apiPaths.welcomeAdminCommentAck,
          data: { commentTime: latestAdminCommentId },
        });
      } catch (ackErr) {
        toast.error(
          "Message sent to Slack, but saving your confirmation failed. Try sending again or contact support."
        );
        console.error("welcome-ack failed:", ackErr);
        return;
      }

      toast.success("Message sent to Slack successfully!");

      setShowWelcomeModal(false);
      setCommentMessage("");
      setWelcomeMessageSent(true);
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to send message");
      console.error("Error in handleSendCommentToSlack:", error);
    } finally {
      setIsSendingComment(false);
    }
  };

  const handleSelectedId = (id: string) => {
    setShowModal(true);
    setSelectedId(id);
  };

  const handleStatusChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
    row: any
  ) => {
    if (event.target.value === "finished") {
      if (row.isUserVideoReq) {
        setSelectedTaskForVideo(row);
        setShowScreenRecorder(true);
      } else {
        handleSelectStatus(event, row);
      }
    } else {
      handleSelectStatus(event, row);
    }
  };

  const handleVideoSave = (videoUrl: string) => {
    if (selectedTaskForVideo) {
      const updatedTask = {
        ...selectedTaskForVideo,
        userVideoUrl: videoUrl,
      };
      handleSelectStatus({ target: { value: "finished" } } as any, updatedTask);
      setShowScreenRecorder(false);
      setSelectedTaskForVideo(null);
    }
  };

  const isOverdueTask = (task: any): boolean => {
    if (!task?.dueDate) return false;
  
    const today = new Date();
    today.setHours(0, 0, 0, 0);
  
    const dueDate = new Date(task.dueDate);
    dueDate.setHours(0, 0, 0, 0);
  
    return (
      dueDate < today &&
      !["finished", "backLog"].includes(task.status)
    );
  };

  const todayColumns: Column[] = [
    {
      field: "projectId",
      renderCell: (params: any) => <p>{params?.value?.name}</p>,
      headerName: "Project",
      width: 50,
      align: "center",
    },
    {
      field: "description",
      headerName: "Task",
      width: 50,
      align: "center",
      renderCell: (params: any) => (
        <div>
          <p>{params.value}</p>
        </div>
      ),
    },
    {
      field: "createdAt",
      renderCell: (params: any) => (
        <div className="flex items-center justify-center gap-2">
          {isOverdueTask(params.row) && (
            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded font-semibold">
              OVERDUE
            </span>
          )}
          <p>{formatDate(params?.value)}</p>
        </div>
      ),
      headerName: "Created At",
      width: 50,
      align: "center",
    },
    {
      field: "priority",
      renderCell: (params: any) => (
        <p className={`${stylePriority[params.value]} text-black`}>
          {params.value}
        </p>
      ),
      headerName: "Priority",
      width: 50,
      align: "center",
    },
    {
      field: "Actions",
      renderCell: (params: any) => (
        <div className={style[params.row.status] || "bg-gray-200"}>
          <select
            defaultValue={params.row.status}
            onChange={(event) => handleStatusChange(event, params.row)}
            className="appearance-none bg-transparent outline-none w-full cursor-pointer font-semibold"
          >
            <option value="todo">To Do</option>
            <option value="in progress">In Progress</option>
            <option value="query">Query</option>
            <option value="finished">Finished</option>
            <option value="backLog">Back Log</option>
          </select>
        </div>
      ),
      headerName: "Status",
      width: 50,
      align: "center",
    },
    {
      field: "Actions",
      headerName: "Actions",
      width: 50,
      align: "center",
      renderCell: (params: any) => (
        <div className="flex space-x-2 justify-center">
          <button
            onClick={() => handleEdit(params.row._id)}
            className="text-fontColor hover:text-primary-2"
          >
            <EditImg />
          </button>
          <button
            onClick={() => handleSelectedId(params.row)}
            className="text-blue-900 hover:text-primary-2 "
          >
            <Chat />
          </button>
          {params.row.videoUrl && (
            <button
              onClick={() => handleVideoClick(params.row.videoUrl)}
              className="text-green-600 hover:text-green-700"
            >
              <VideoIcon />
            </button>
          )}
          {params.row.userVideoUrl && (
            <button
              onClick={() => handleVideoClick(params.row.userVideoUrl)}
              className="text-green-600 hover:text-green-700"
            >
              <UserPlayIcon />
            </button>
          )}
        </div>
      ),
    },
  ];

  const yesterdayColumns: Column[] = [
    {
      field: "projectId",
      renderCell: (params: any) => <p>{params?.value?.name}</p>,
      headerName: "Project",
      width: 50,
      align: "center",
    },
    {
      field: "description",
      headerName: "task",
      width: 50,
      align: "center",
      renderCell: (params: any) => (
        <div className="flex items-center gap-2">
          <p>{params.value}</p>
        </div>
      ),
    },
    {
      field: "Actions",
      renderCell: (params: any) => (
        <>
          <p>{formatDate(params?.row?.dueDate)}</p>
          <div className={style[params.row.status] || "bg-gray-200"}>
            <select
              defaultValue={params.row.status}
              onChange={(event) => handleSelectStatus(event, params.row)}
              className="appearance-none bg-transparent outline-none w-full cursor-pointer font-semibold"
            >
              <option value="todo">To Do</option>
              <option value="in progress">In Progress</option>
              <option value="query">Query</option>
              <option value="finished">Finished</option>
              <option value="backLog">Back Log</option>
            </select>
          </div>
          <div className="flex space-x-2 mt-2 justify-center">
            <button
              onClick={() => handleSelectedId(params.row)}
              className="text-blue-900 hover:text-primary-2 "
            >
              <Chat />
            </button>
            {params.row.videoUrl && (
              <button
                onClick={() => handleVideoClick(params.row.videoUrl)}
                className="text-blue-600 hover:text-blue-700"
              >
                <VideoIcon />
              </button>
            )}
            {params.row.userVideoUrl && (
              <button
                onClick={() => handleVideoClick(params.row.userVideoUrl)}
                className="text-green-600 hover:text-green-700"
              >
                <UserPlayIcon />
              </button>
            )}
          </div>
        </>
      ),
      headerName: "Status",
      width: 50,
      align: "center",
    },
  ];

  const formatTaskData = (allTasks: any[]): string => {
    // Helper function to format date as MM.dd.yyyy
    const formatDateMMDDYYYY = (date: string | Date): string => {
      try {
        const d = new Date(date);
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const year = d.getFullYear();
        return `${month}.${day}.${year}`;
      } catch {
        return "Invalid Date";
      }
    };

    

    // Helper function to determine day type
    const getDayType = (task: any): string => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (task.dueDate) {
        const dueDate = new Date(task.dueDate);
        dueDate.setHours(0, 0, 0, 0);

        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        // Check if task is overdue
        if (dueDate < today && !["finished", "backLog"].includes(task.status)) {
          return "Overdue";
        } else if (dueDate.getTime() === today.getTime()) {
          return "Today";
        } else if (dueDate.getTime() === yesterday.getTime()) {
          return "Pending";
        } else if (dueDate.getTime() === tomorrow.getTime()) {
          return "Upcoming";
        } else if (dueDate < today) {
          return "Backlog";
        } else {
          return "Upcoming";
        }
      }

      // If no dueDate, check createdAt
      if (task.createdAt) {
        const createdDate = new Date(task.createdAt);
        createdDate.setHours(0, 0, 0, 0);

        if (createdDate.getTime() === today.getTime()) {
          return "Today";
        } else if (createdDate < today) {
          return "Backlog";
        } else {
          return "Upcoming";
        }
      }

      return "Today";
    };

    // Helper function to get priority emoji
    const getPriorityEmoji = (priority: string): string => {
      const priorityLower = priority?.toLowerCase() || "";
      if (priorityLower === "urgent" || priorityLower === "high") {
        return "🔴";
      }
      return "";
    };

    // Helper function to get priority sort order (lower number = higher priority)
    const getPrioritySortOrder = (priority: string): number => {
      const priorityLower = priority?.toLowerCase() || "";
      if (priorityLower === "urgent") return 0;
      if (priorityLower === "high") return 1;
      if (priorityLower === "medium") return 2;
      if (priorityLower === "low") return 3;
      return 4;
    };

    // Group tasks by date (createdAt) first
    const tasksByDate: { [key: string]: any[] } = {};
    allTasks.forEach((task: any) => {
      const dateKey = formatDateMMDDYYYY(task.createdAt || new Date());
      if (!tasksByDate[dateKey]) {
        tasksByDate[dateKey] = [];
      }
      tasksByDate[dateKey].push(task);
    });

    // Format data according to the required format
    const formattedData = Object.keys(tasksByDate)
      .sort()
      .map((dateKey) => {
        const tasks = tasksByDate[dateKey];

        // Sort ALL tasks by priority first (urgent/high on top), regardless of project
        tasks.sort((a, b) => {
          const priorityA = getPrioritySortOrder(a.priority);
          const priorityB = getPrioritySortOrder(b.priority);
          return priorityA - priorityB;
        });

        // Group tasks by project (maintaining priority sort order)
        const tasksByProject: { [key: string]: any[] } = {};
        const projectOrder: string[] = [];

        tasks.forEach((task: any) => {
          const projectName = task.projectId?.name || "N/A";
          if (!tasksByProject[projectName]) {
            tasksByProject[projectName] = [];
            projectOrder.push(projectName);
          }
          tasksByProject[projectName].push(task);
        });

        // Format each project's tasks
        const projectSections = projectOrder
          .map((projectName) => {
            const projectTasks = tasksByProject[projectName];

            const taskLines = projectTasks
              .map((task: any, index: number) => {
                const description = task.description || "N/A";
                const status = task.status || "N/A";
                const dayType = getDayType(task);
                const queryMessage = task.query || "";
                const priority = task.priority || "";
                const priorityEmoji = getPriorityEmoji(priority);

                // Add overdue emoji if task is overdue
                const overdueEmoji = dayType === "Overdue" ? "⚠️ " : "";

                let taskLine = `${index + 1}. ${overdueEmoji}${priorityEmoji ? priorityEmoji + " " : ""
                  }Task: ${description} - ${status} - ${dayType}`;

                // Add priority label for urgent/high priority
                if (priority?.toLowerCase() === "urgent") {
                  taskLine += ` - urgent`;
                } else if (priority?.toLowerCase() === "high") {
                  taskLine += ` - high priority`;
                }

                if (status === "query" && queryMessage) {
                  taskLine += `\n   Query: ${queryMessage}`;
                }

                return taskLine;
              })
              .join("\n");

            return `          Project: ${projectName}\n${taskLines}`;
          })
          .join("\n");

        return `Task ${dateKey}\n${projectSections}`;
      })
      .join("\n\n");

    return formattedData;
  };

  const tomorrowColumns: Column[] = [
    {
      field: "projectId",
      renderCell: (params: any) => <p>{params?.value?.name}</p>,
      headerName: "Project",
      width: 50,
      align: "center",
    },
    {
      field: "description",
      headerName: "task",
      width: 50,
      align: "center",
    },
    {
      field: "Actions",
      renderCell: (params: any) => (
        <>
          <p>{formatDate(params?.row?.dueDate)}</p>
          <div className={style[params.row.status] || "bg-gray-200"}>
            <select
              defaultValue={params.row.status}
              onChange={(event) => handleSelectStatus(event, params.row)}
              className="appearance-none bg-transparent outline-none w-full cursor-pointer font-semibold"
            >
              <option value="todo">To Do</option>
              <option value="in progress">In Progress</option>
              <option value="query">Query</option>
              <option value="finished">Finished</option>
              <option value="backLog">Back Log</option>
            </select>
          </div>
          <div className="flex space-x-2 mt-2 justify-center">
            <button
              onClick={() => handleSelectedId(params.row)}
              className="text-blue-900 hover:text-primary-2 "
            >
              <Chat />
            </button>
            {params.row.videoUrl && (
              <button
                onClick={() => handleVideoClick(params.row.videoUrl)}
                className="text-blue-600 hover:text-blue-700"
              >
                <VideoIcon />
              </button>
            )}
            {params.row.userVideoUrl && (
              <button
                onClick={() => handleVideoClick(params.row.userVideoUrl)}
                className="text-green-600 hover:text-green-700"
              >
                <UserPlayIcon />
              </button>
            )}
          </div>
        </>
      ),
      headerName: "Status",
      width: 50,
      align: "center",
    },
  ];

  const backLogColumns: Column[] = [
    {
      field: "createdAt",
      renderCell: (params: any) => <p>{formatDate(params?.value)}</p>,
      headerName: "Date",
      width: 50,
      align: "center",
    },
    {
      field: "description",
      headerName: "Task",
      width: 50,
      align: "center",
    },
    {
      field: "Actions",
      renderCell: (params: any) => (
        <>
          <p>{formatDate(params?.row?.dueDate)}</p>
          <div className={style[params.row.status] || "bg-gray-200"}>
            <select
              defaultValue={params.row.status}
              onChange={(event) => handleSelectStatus(event, params.row)}
              className="appearance-none bg-transparent outline-none w-full cursor-pointer font-semibold"
            >
              <option value="todo">To Do</option>
              <option value="in progress">In Progress</option>
              <option value="query">Query</option>
              <option value="finished">Finished</option>
              <option value="backLog">Back Log</option>
            </select>
          </div>
        </>
      ),
      headerName: "Status",
      width: 50,
      align: "center",
    },
  ];

  return (
    <div>
      <div className="m-5 bg-white h-[80vh] overflow-y-auto ">
        {loading && <Loader />}
        <div className="m-1">
          {selectedTaskSheetId ? (
            <EditTaskSheetIndex
              taskSheetId={selectedTaskSheetId}
              setMethod={() => updateScreen("edit")}
            />
          ) : (
            <>
              <div className="">
                <div className="mt-1">
                  <div className="flex justify-between">
                    <div className="w-1/2">
                      <h1 className="text-xl font-bold mb-4">Admin Comments</h1>
                      <div className="space-y-4">
                        {adminComment?.map(
                          (
                            comment: {
                              formattedCommentTime: string;
                              comments: string;
                            },
                            index: number
                          ) => (
                            <div key={index} className="border rounded p-3">
                              <div className="text-sm text-gray-600 mb-2">
                                {comment?.formattedCommentTime}
                              </div>
                              <p>{comment?.comments}</p>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mr-4">
                      <div className="bg-white rounded-lg p-4 border">
                        <h2 className="text-lg font-bold mb-3 text-gray-800">
                          Overall Task Summary
                        </h2>
                        <div className="space-y-2">
                          <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-600">ToDo</span>
                            <span className="font-medium">
                              {taskSummary.overall.todo}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-600">In Progress</span>
                            <span className="font-medium">
                              {taskSummary.overall.inProgress}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-600">Query</span>
                            <span className="font-medium">
                              {taskSummary.overall.query}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-600">Finished</span>
                            <span className="font-medium">
                              {taskSummary.overall.finished}
                            </span>
                          </div>
                          <div className="flex justify-between py-1  border-gray-100">
                            <span className="text-gray-600">BackLog</span>
                            <span className="font-medium">
                              {taskSummary.overall.backLog}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 mt-2 pt-2 border-t border-gray-200">
                            <span className="font-medium">Total Tasks</span>
                            <span className="font-bold">
                              {taskSummary.overall.totalTasks}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4 border">
                        <h2 className="text-lg font-bold mb-3 text-gray-800">
                          Today's Task Summary
                        </h2>
                        <div className="space-y-2">
                          <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-600">ToDo</span>
                            <span className="font-medium">
                              {taskSummary.today.todo}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-600">In Progress</span>
                            <span className="font-medium">
                              {taskSummary.today.inProgress}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-600">Query</span>
                            <span className="font-medium">
                              {taskSummary.today.query}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 border-b border-gray-100">
                            <span className="text-gray-600">Finished</span>
                            <span className="font-medium">
                              {taskSummary.today.finished}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 border-gray-100">
                            <span className="text-gray-600">BackLog</span>
                            <span className="font-medium">
                              {taskSummary.today.backLog}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 mt-2 pt-2 border-t border-gray-200">
                            <span className="font-medium">
                              Total Today's Tasks
                            </span>
                            <span className="font-bold">
                              {taskSummary.today.totalTasks}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grid justify-items-end -mb-5 mt-3 mr-5">
                  </div>
                  <div className="flex gap-3 mt-5">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleCopyAllData();
                      }}
                      disabled={isSending}
                      className={`px-4 py-2 rounded-md text-white transition ${isSending
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-500 hover:bg-blue-600"
                        }`}
                      type="button"
                    >
                      Copy Data
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleSendToSlack();
                      }}
                      disabled={isSending}
                      className={`px-4 py-2 rounded-md text-white transition ${isSending
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-green-500 hover:bg-green-600"
                        }`}
                      type="button"
                    >
                      {isSending ? "Sending..." : "Send to Slack"}
                    </button>
                  </div>
                  <div className="bg-green-100 m-[0.5%] rounded-lg mt-3">
                    <CommonDataGrid
                      tableName="Today Task"
                      rows={taskSheetData}
                      columns={todayColumns}
                      getRowId={(row) => row._id}
                      showConfirmation={showConfirmation}
                      confirmationMessage="Are you sure you want to Status Update this TaskSheet?"
                      onCancelDelete={handleCancelStatusUp}
                      onConfirmDelete={() => { }}
                      loading={!taskSheetData}
                      addButton
                      onDateFilter={handleDateFilter}
                      setSearchBar={setSearch}
                      setCurrentPage={setPage}
                      currentPage={page}
                      totalPages={totalPage}
                      downloadCSVFilter={false}
                      dayFilter={false}
                      searchFilter={false}
                      mainStyle=""
                      defaultDateNumber={"2"}
                    />
                  </div>
                </div>
                <div className="flex justify-between  m-2 gap-4">
                  <div className="bg-yellow-100 rounded-lg w-full">
                    <CommonDataGrid
                      tableName="Pending Task"
                      rows={yesterDayData}
                      columns={yesterdayColumns}
                      getRowId={(row) => row._id}
                      showConfirmation={showConfirmation}
                      confirmationMessage="Are you sure you want to Status Update this TaskSheet?"
                      onCancelDelete={handleCancelStatusUp}
                      onConfirmDelete={() => { }}
                      loading={!yesterDayData}
                      onDateFilter={handleYesterDay}
                      setSearchBar={setSearch}
                      setCurrentPage={setYesterPage}
                      currentPage={yesterPage}
                      totalPages={yesterDayPage}
                      downloadCSVFilter={false}
                      dayFilter={false}
                      searchFilter={false}
                      mainStyle=""
                      defaultDateNumber={"1"}
                    />
                  </div>
                  <div className="bg-blue-100 rounded-lg w-full">
                    <CommonDataGrid
                      tableName="Up Coming Task"
                      rows={tomorrowDayData}
                      columns={tomorrowColumns}
                      getRowId={(row) => row._id}
                      showConfirmation={showConfirmation}
                      confirmationMessage="Are you sure you want to Status Update this TaskSheet?"
                      onCancelDelete={handleCancelStatusUp}
                      onConfirmDelete={() => { }}
                      loading={!tomorrowDayData}
                      onDateFilter={handleTomorrowDay}
                      setSearchBar={setSearch}
                      setCurrentPage={setTomorrowPage}
                      currentPage={tomorrowPage}
                      totalPages={tomorrowDayPage}
                      downloadCSVFilter={false}
                      dayFilter={false}
                      searchFilter={false}
                      mainStyle=""
                      defaultDateNumber={"3"}
                    />
                  </div>
                  <div className="bg-red-100 rounded-lg w-full">
                    <CommonDataGrid
                      tableName="Back Log"
                      rows={backLogData}
                      columns={backLogColumns}
                      getRowId={(row) => row._id}
                      showConfirmation={showConfirmation}
                      confirmationMessage="Are you sure you want to Status Update this TaskSheet?"
                      onCancelDelete={handleCancelStatusUp}
                      onConfirmDelete={() => { }}
                      loading={!backLogData}
                      onDateFilter={handleBackLog}
                      setSearchBar={setSearch}
                      setCurrentPage={setBackPage}
                      currentPage={backPage}
                      totalPages={backLogPage}
                      downloadCSVFilter={false}
                      mainStyle=""
                      dayFilter={false}
                      searchFilter={false}
                      defaultDateNumber={"1"}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showModal && (
        <Modal isOpen={showModal} onClose={handleModalClose}>
          <div className="p-4">
            <h2 className="text-xl font-bold mb-4">Task Query</h2>
            <textarea
              className="w-full p-2 border rounded"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description..."
            />

            {/* Send Message to Slack Section in Modal */}
            {/* {adminComment && adminComment.length > 0 && (
              <div className="mt-6 border-t pt-4">
                <h2 className="text-lg font-semibold mb-3">
                  Send Message to Slack
                </h2>
                <div className="space-y-3">
                  <textarea
                    value={commentMessage}
                    onChange={(e) => setCommentMessage(e.target.value)}
                    placeholder="Type your message to send to Slack..."
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows={3}
                  />
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSendCommentToSlack(e);
                    }}
                    disabled={isSendingComment || !commentMessage.trim()}
                    className={`w-full px-4 py-2 rounded-md text-white transition ${
                      isSendingComment || !commentMessage.trim()
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-purple-500 hover:bg-purple-600"
                    }`}
                    type="button"
                  >
                    {isSendingComment ? "Sending..." : "Send to Slack"}
                  </button>
                </div>
              </div>
            )} */}

            <div className="flex justify-end mt-4 space-x-2">
              <button
                className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-100"
                onClick={handleModalClose}
                type="button"
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700"
                onClick={(e) => {
                  e.preventDefault();
                  handleModalSubmit();
                  if (selectedId) {
                    sendSlackMessage(selectedId);
                  }
                }}
                type="button"
              >
                Submit
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showWelcomeModal && (
        <Modal
          isOpen={showWelcomeModal}
          onClose={() => {
            toast.error("Please send the message before closing");
          }}
        >
          <div className="p-4">
            <h2 className="text-xl font-bold mb-4">Welcome Back!</h2>
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">
                Latest Admin Comment:
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">{latestAdminComment}</p>
              </div>
            </div>
            {adminComment && adminComment.length > 0 && (
              <div className="mt-6 border-t pt-4">
                <h2 className="text-lg font-semibold mb-3">
                  Send Message to Slack
                </h2>
                <div className="space-y-3">
                  <textarea
                    value={commentMessage}
                    onChange={(e) => setCommentMessage(e.target.value)}
                    placeholder="Type your message to send to Slack..."
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows={3}
                  />
                  <button
                    onClick={handleSendCommentToSlack}
                    disabled={
                      isSendingComment ||
                      welcomeMessageSent ||
                      !commentMessage.trim()
                    }
                    className={`px-4 py-2 rounded-md text-white transition ${isSendingComment ||
                        welcomeMessageSent ||
                        !commentMessage.trim()
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-purple-500 hover:bg-purple-600"
                      }`}
                  >
                    {welcomeMessageSent
                      ? "Message Sent ✅"
                      : isSendingComment
                        ? "Sending..."
                        : "Send to Slack"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoUrl={selectedVideoUrl}
        title="Task Video"
      />

      {showScreenRecorder && (
        <Modal
          isOpen={showScreenRecorder}
          onClose={() => setShowScreenRecorder(false)}
        >
          <div className="p-4">
            <h2 className="text-xl font-bold mb-4">
              Record Task Completion Video
            </h2>
            <ScreenRecorder onSave={handleVideoSave} />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TaskSheet;
